import React, { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

function ImportChildrenForm({ apiBase, authHeaders, groups, onImported }) {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [importName, setImportName] = useState("");
  const [groupId, setGroupId] = useState("none");
  const [importType, setImportType] = useState("neu");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);

  // Datei-Vorschau laden
  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    
    if (f && f.name.toLowerCase().endsWith('.xlsx')) {
      // Einfache Vorschau: Zeige Datei-Info
      setPreview({
        name: f.name,
        size: (f.size / 1024).toFixed(1) + ' KB',
        lastModified: new Date(f.lastModified).toLocaleDateString('de-DE')
      });
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) {
      toast({ title: "Bitte eine XLSX-Datei auswählen", variant: "destructive" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    if (importName) formData.append("name", importName);
    if (groupId && groupId !== "none") formData.append("group_id", groupId);
    if (importType) formData.append("type", importType);

    setIsSubmitting(true);
    try {
      // Sichere Header-Extraktion - authHeaders kann Funktion oder undefined sein
      let headers = {};
      if (typeof authHeaders === 'function') {
        const authResult = authHeaders();
        headers = authResult?.headers || {};
      }
      
      const res = await axios.post(`${apiBase}/api/import/children`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });
      toast({
        title: "Import erfolgreich!",
        description: `${res.data.row_count || 0} Kinder wurden importiert${importType === 'neu' ? ' und als NEU markiert' : ''}.`,
      });
      setFile(null);
      setImportName("");
      setImportType("neu");
      setGroupId("none");
      setPreview(null);
      // Reset file input
      const fileInput = document.querySelector('[data-testid="import-file-input"]');
      if (fileInput) fileInput.value = '';
      if (onImported) onImported();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Import ist fehlgeschlagen. Bitte Datei prüfen und erneut versuchen.";
      toast({ title: "Fehler beim Import", description: msg, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  }

  // Gruppe 1 finden (für Standard-Vorschlag)
  const gruppe1 = groups.find(g => g.name?.toLowerCase().includes('gruppe 1') || g.name === 'Gruppe 1');

  return (
    <div className="space-y-6" data-testid="import-form-container">
      {/* Info-Box */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
        <h4 className="font-semibold text-blue-900 mb-2" style={{ fontSize: 'var(--font-body-size)' }}>
          📥 Kinder aus Yolawo importieren
        </h4>
        <p className="text-blue-700 mb-2" style={{ fontSize: 'var(--font-small-size)' }}>
          Exportieren Sie die Kursbuchungen aus Yolawo als XLSX-Datei und importieren Sie die neuen Kinder hier.
        </p>
        <ul className="list-disc list-inside text-blue-700 space-y-1" style={{ fontSize: 'var(--font-small-size)' }}>
          <li>Neue Kinder werden mit "NEU" markiert</li>
          <li>NEU-markierte Kinder werden bei Gruppenverschiebungen nicht berücksichtigt</li>
          <li>Standardmäßig werden neue Kinder der Gruppe 1 zugeordnet</li>
        </ul>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit} data-testid="import-form">
        {/* Datei-Upload */}
        <div className="rounded-xl bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label htmlFor="import-file" className="font-semibold" style={{ fontSize: 'var(--font-body-size)' }}>
              1. XLSX-Datei auswählen
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await axios.get(`${apiBase}/api/import/children/template`, {
                    responseType: "blob",
                    ...(authHeaders ? authHeaders() : {}),
                  });
                  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = "Muster_Kinderimport.xlsx";
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                } catch (e) {
                  console.error(e);
                  toast({
                    title: "Download fehlgeschlagen",
                    description: "Die Musterdatei konnte nicht geladen werden.",
                    variant: "destructive",
                  });
                }
              }}
              data-testid="import-template-download"
              style={{ fontSize: 'var(--font-small-size)' }}
            >
              📄 Musterdatei herunterladen
            </Button>
          </div>
          
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-violet-300 transition-colors">
            <Input
              id="import-file"
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleFileChange}
              className="cursor-pointer"
              data-testid="import-file-input"
            />
          </div>

          {/* Datei-Vorschau */}
          {preview && (
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-medium text-emerald-900" style={{ fontSize: 'var(--font-body-size)' }}>{preview.name}</p>
                <p className="text-emerald-600" style={{ fontSize: 'var(--font-small-size)' }}>
                  {preview.size} • Geändert: {preview.lastModified}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  const fileInput = document.querySelector('[data-testid="import-file-input"]');
                  if (fileInput) fileInput.value = '';
                }}
                className="text-slate-400 hover:text-rose-500 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <p className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
            Erwartete Spalten: <strong>Vorname, Nachname, E-Mail, Telefon, Geburtsdatum</strong>
          </p>
        </div>

        {/* Gruppe auswählen */}
        <div className="rounded-xl bg-white p-4 shadow-sm space-y-3">
          <Label className="font-semibold" style={{ fontSize: 'var(--font-body-size)' }}>
            2. Ziel-Gruppe auswählen
          </Label>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="import-group-select" className="w-full">
              <SelectValue placeholder="Gruppe auswählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-slate-500">Keine Gruppe zuordnen</span>
              </SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: g.color || '#e5e7eb' }}
                    />
                    <span>{g.name}</span>
                    {g.name?.toLowerCase().includes('gruppe 1') && (
                      <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Empfohlen für neue Kinder</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
            Neue Kinder werden üblicherweise der <strong>Gruppe 1</strong> zugeordnet.
          </p>
        </div>

        {/* Import-Typ */}
        <div className="rounded-xl bg-white p-4 shadow-sm space-y-3">
          <Label className="font-semibold" style={{ fontSize: 'var(--font-body-size)' }}>
            3. Import-Typ wählen
          </Label>
          
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Option: Neu */}
            <label 
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                importType === "neu" 
                  ? "border-emerald-500 bg-emerald-50" 
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="import-type"
                value="neu"
                checked={importType === "neu"}
                onChange={() => setImportType("neu")}
                className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                data-testid="import-type-neu"
              />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900" style={{ fontSize: 'var(--font-body-size)' }}>
                    🆕 Neue Kinder
                  </span>
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-0.5 rounded-full">
                    Empfohlen
                  </span>
                </div>
                <p className="text-slate-500 mt-1" style={{ fontSize: 'var(--font-small-size)' }}>
                  Kinder werden als <strong>NEU</strong> markiert. Diese Markierung zeigt an, dass es sich um 
                  tatsächlich neue Kursteilnehmer handelt, die in ihrer Gruppe verbleiben sollen.
                </p>
              </div>
            </label>

            {/* Option: Bestand */}
            <label 
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                importType === "bestand" 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="import-type"
                value="bestand"
                checked={importType === "bestand"}
                onChange={() => setImportType("bestand")}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                data-testid="import-type-bestand"
              />
              <div>
                <span className="font-semibold text-slate-900" style={{ fontSize: 'var(--font-body-size)' }}>
                  📋 Bestandsdaten
                </span>
                <p className="text-slate-500 mt-1" style={{ fontSize: 'var(--font-small-size)' }}>
                  Für den Import bestehender Daten (z.B. Migration). Kinder werden <strong>nicht</strong> als 
                  NEU markiert.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Import-Name (optional) */}
        <div className="rounded-xl bg-white p-4 shadow-sm space-y-3">
          <Label htmlFor="import-name" className="font-semibold" style={{ fontSize: 'var(--font-body-size)' }}>
            4. Import benennen <span className="font-normal text-slate-400">(optional)</span>
          </Label>
          <Input
            id="import-name"
            value={importName}
            onChange={(e) => setImportName(e.target.value)}
            placeholder="z.B. Kurs Frühjahr 2026"
            data-testid="import-name-input"
          />
          <p className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>
            Ein Name hilft, den Import später in der Historie wiederzufinden.
          </p>
        </div>

        {/* Zusammenfassung & Submit */}
        <div className="rounded-xl bg-gradient-to-r from-violet-50 to-white p-4 shadow-sm">
          <h4 className="font-semibold text-slate-900 mb-3" style={{ fontSize: 'var(--font-body-size)' }}>
            Zusammenfassung
          </h4>
          <div className="space-y-2 mb-4" style={{ fontSize: 'var(--font-body-size)' }}>
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${file ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                {file ? '✓' : '○'}
              </span>
              <span className={file ? 'text-slate-900' : 'text-slate-400'}>
                Datei: {file ? preview?.name : 'Nicht ausgewählt'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${groupId !== 'none' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                {groupId !== 'none' ? '✓' : '○'}
              </span>
              <span className={groupId !== 'none' ? 'text-slate-900' : 'text-slate-400'}>
                Gruppe: {groupId !== 'none' ? groups.find(g => g.id === groupId)?.name : 'Keine ausgewählt'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs bg-emerald-100 text-emerald-600">✓</span>
              <span className="text-slate-900">
                Typ: {importType === 'neu' ? '🆕 Neue Kinder (NEU-Markierung)' : '📋 Bestandsdaten'}
              </span>
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting || !file} 
            className="w-full"
            data-testid="import-submit-button"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                Import läuft...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import starten
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Hinweis zur NEU-Markierung */}
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
        <h4 className="font-semibold text-amber-900 mb-2" style={{ fontSize: 'var(--font-body-size)' }}>
          💡 Warum die NEU-Markierung wichtig ist
        </h4>
        <p className="text-amber-700" style={{ fontSize: 'var(--font-small-size)' }}>
          Mit jedem neuen Schwimmkurs kommen neue Kinder in die <strong>Gruppe 1</strong>. 
          Gleichzeitig können in Gruppe 1 aber auch Kinder sein, die den Kurs wiederholen oder 
          deren Gruppenverschiebung noch aussteht. Die <strong>NEU-Markierung</strong> macht klar, 
          welche Kinder tatsächlich neu sind und in Gruppe 1 verbleiben sollen - unabhängig von 
          späteren Gruppenumstrukturierungen.
        </p>
      </div>
    </div>
  );
}

export default ImportChildrenForm;
